import { usePageApi } from "./PageApi";
import { translate } from "../../../utils/translate";
import { apiUrls } from "../../../utils/api";
import { Button, FormControl, InputLabel, Select, TextField, ListItemSecondaryAction, IconButton, ListItemButton } from "@mui/material";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import css from "../../../css/modules/toolbar.module.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { unsavedChangesPopup } from "../utils/utils";
import clsx from "clsx";
import { ChevronIcon } from "../../DepartmentOverview/components/ChevronIcon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import type { Project } from "../index";
import { useDispatch } from "react-redux";
import { add } from "../../../redux/snackbar";
import { useHistory, useLocation } from "react-router-dom";
import { routes } from "../../../utils/constants";
import { toast } from "react-toastify";
import { useApiClient } from '../../../components/ApiProvider';

type Props = {
  overview: Project;
  readOnly: boolean;
};

function areStringsEqual(str1: string, str2: string) {
  if (isNullOrEmptyOrUndefined(str1) || isNullOrEmptyOrUndefined(str2)) {
    return false;
  }
  else {
    return str1.toLowerCase() === str2.toLowerCase();
  }
}

function isNullOrEmptyOrUndefined(str1: string) {
  if (str1 === undefined) {
    return true;
  }
  else if (typeof str1 === "string" && str1.length === 0) {
    return true;
  }
  else {
    return str1 === null;
  }
}

export function BaselineButtons({ overview, readOnly }: Props) {
  const { baselineEnabled } = usePageApi();
  const [baselines, setBaselines] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const onCreateDialogOpen = useCallback(() => setCreateDialogOpen(true), []);
  const onOpenDeleteDialog = useCallback(() => setDeleteDialogOpen(true), []);
  const onCreateDialogClose = useCallback(() => setCreateDialogOpen(false), []);
  const onCloseDeleteDialog = useCallback(() => setDeleteDialogOpen(false), []);
  const [newBaselineName, setNewBaselineName] = useState("");
  const [baselineNameToDelete, setBaselineNameToDelete] = useState("");
  const dispatch = useDispatch();
  const apiClient = useApiClient();
  const baselineToDelete = useRef(null);
  
  const onCreateBaseline = useCallback(async () => {
    if (readOnly) {
      toast.error(translate("The baseline cannot be created, the current user does not have write access."));
      return;
    }
    
    if (newBaselineName.length < 1 || newBaselineName.length > 40) {
      dispatch(
        add({
          severity: "warning",
          id: "invalid-baseline-name",
          message: translate("Baseline name has incorrect length"),
          autoHideDuration: 5000,
        })
      );
      return;
    }

    let doesBaselineNameExists = false;
    for (let i = 0; i < overview.baselineProjects.length; i++) {
      if (areStringsEqual(overview.baselineProjects[i].VersionDescription, newBaselineName)) {
        doesBaselineNameExists = true;
        break;
      }
    }

    if (doesBaselineNameExists) {
      dispatch(
        add({
          severity: "warning",
          id: "invalid-baseline-name",
          message: translate("Baseline name must be unique", { newBaselineName }),
          autoHideDuration: 5000,
        })
      );
      return;
    }

    setCreateDialogOpen(false);
    saveBaseline();

  }, [overview, dispatch, newBaselineName]);

  useEffect(() => {
    if (baselineEnabled && apiClient) {
      (async () => {
        const baselineData = await apiClient.get(apiUrls.GetBaselineProjects, {
          project: overview.projectID
        });
        const baselines = await baselineData.json();
        overview.baselineProjects = baselines;
        setBaselines(baselines);
      })();
    }
  }, [overview, baselineEnabled, apiClient]);

  const onBaselineSelect = useCallback(
    (baselineID) => {
      overview.goToBaselineProject(baselineID);
    },
    [overview]
  );

  const onBaselineDeleteClick = useCallback(
    async (id) => {

      if (readOnly) {
        toast.error(translate("The baseline cannot be deleted, the current user does not have write access."));
        return;
      }

      baselineToDelete.current = id;
      setBaselineNameToDelete(overview.baselineProjects.find(item => item.PK_Project == id)?.VersionDescription);
      onOpenDeleteDialog();

    },
    [apiClient, overview]
  );

  
  const doBaselineDelete = useCallback(
    async () => {
      const resp = (await apiClient.post(apiUrls.DeleteSimulation, undefined, {
      project: baselineToDelete.current })) as Response;

      const result = await resp.json();
      if (result.error) {
        toast.error(translate(result.message));
      } else {
        toast.success(translate(result.message));
        overview.baselineProjects.splice(overview.baselineProjects.findIndex(item => item.PK_Project == baselineToDelete.current ), 1);
        overview.setState({ baselines: overview.baselineProjects });
      }
      onCloseDeleteDialog();
    },
    [apiClient, overview]
  );

  const saveBaseline = async () => {
    if (overview.state.hasChanges) {
      const { save, cancel, ignore } = await unsavedChangesPopup(translate("baselineUnsavedContent"));
      if (cancel) {
        return;
      }

      if (save) {
        await overview.saveProject();
      }

      if (ignore) {
        await new Promise((res) => overview.setState({ hasChanges: false }, () => res(null)));
      }
    }
    overview.setState({ loading: true });

    const resp = (await apiClient.post(apiUrls.StartProjectSimulation, undefined, {
      MainProjectID: (overview.state.currentProject as Record<string, string> | null)?.FK_MainProject,
      VersionDescription: newBaselineName,
      baseline: true,
    })) as Response;

    const project = await resp.json();
    overview.goToBaselineProject(project.PK_Project);
  }

  const history = useHistory();
  const location = useLocation();
  const onClear = () => {
    history.push({
      pathname: routes.replaceRedirect,
      state: {
        target: location.pathname,
      },
    });
  };

  if (!baselineEnabled) {
    return null;
  }

  return (
    <>
      <FormControl className={clsx(css.formControl, css.input, css.baselineSelect)} >
        <InputLabel htmlFor="baseline-select">{translate("Select baseline")}</InputLabel>
        <Select
          IconComponent={(props) => ChevronIcon(onClear, props)}
          disabled={!baselines?.length}
          autoWidth
          id="baseline-select"
          labelId="baseline-select"
          value={overview.baselineProjects?.length ? overview.baselineProject || "" : ""}      
        >
          {(overview.baselineProjects || []).map((i) => {
            return (

              <ListItem key={i.PK_Project} value={i.PK_Project} >

                <ListItemButton dense={true} sx={{ padding: 0, margin: 0 }}>
                  <ListItemText sx={{ padding: 0, margin: 0 }} onClick={() => onBaselineSelect(i.PK_Project)}>{i.VersionDescription}</ListItemText>
                </ListItemButton>

                {(i.PK_Project != overview.baselineProject) &&
                  (
                  <ListItemSecondaryAction onClick={() => onBaselineDeleteClick(i.PK_Project)}>
                    {!readOnly && <IconButton edge="end" aria-label="delete"><DeleteOutlinedIcon /></IconButton>}
                  </ListItemSecondaryAction>
                  )}

              </ListItem>
            );
          })}
        </Select>
      </FormControl>     

      {!readOnly && <Button onClick={onCreateDialogOpen} size="small" variant="outlined" data-testid="create-baseline-button">
        {translate("Create baseline")}
      </Button>}
      <Dialog open={createDialogOpen} onClose={onCreateDialogClose}>
        <DialogContent>
          <h4>{translate("Enter baseline name")}</h4>
          <TextField fullWidth inputRef={inputRef} label={translate("Baseline name")} value={newBaselineName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNewBaselineName(event.target.value);
            }} />
          <div className={css.baselineNameCreateBtn}>
            <Button variant="outlined" onClick={onCreateBaseline}>
              {translate("Create")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    <Dialog open={deleteDialogOpen} onClose={onCloseDeleteDialog}>
      <DialogTitle id="alert-dialog-title">
          {translate("Delete baseline")}
        </DialogTitle>
         <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {translate("Are you sure you want to delete baseline ' ' ?", { baselineNameToDelete } )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseDeleteDialog}>{translate("Cancel")} </Button>
          <Button onClick={doBaselineDelete} autoFocus>
            {translate("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
